/**
 * IsolatedTextarea — Textarea memoizzata con state locale + debounce.
 *
 * Introdotta il 15/07/2026 come mitigazione mirata dei rallentamenti
 * da digitazione identificati nell'audit prestazionale
 * (`/app/memory/PERFORMANCE_AUDIT.md` — Cause #1 e #3).
 *
 * PROBLEMA RISOLTO
 * ----------------
 * Nei modal di Diario Giornaliero e Note su Pratiche i textarea erano
 * controllati direttamente dal parent (`setEditForm({...f, content: v})`
 * o `setEditText(v)`). Ogni carattere digitato:
 *   1. Ricreava l'oggetto di state del parent
 *   2. Invalidava tutti gli useMemo che dipendono da esso
 *   3. Ri-renderizzava l'intero modal (8+ input, mapper storia, liste)
 *
 * SOLUZIONE
 * ---------
 * Il componente mantiene lo state DENTRO di sé (unstoppable typing) e
 * propaga al parent SOLO:
 *   - dopo `debounceMs` di inattività (default 300ms), oppure
 *   - immediatamente al blur (utente esce dal campo), oppure
 *   - immediatamente se il parent richiama `flushRef.current()`
 *     (utile in save handler per ottenere l'ultimo valore).
 *
 * Il componente accetta cambi di `value` esterni (es. caricamento di
 * una nota diversa) e si allinea allo state locale — coerenza garantita.
 *
 * USO
 * ---
 *   <IsolatedTextarea
 *     value={editForm.content}
 *     onCommit={(v) => setEditForm((f) => ({ ...f, content: v }))}
 *     rows={4}
 *     placeholder="…"
 *     data-testid="…"
 *   />
 *
 * ATTENZIONE: il parent deve chiamare l'ultima versione (post-blur)
 * PRIMA di salvare. Il pattern standard: onClick di Save fa blur del
 * textarea (che triggera onCommit), poi legge il valore dal parent
 * state. In alternativa il parent può usare `useRef` + `flushRef`.
 */

import React, { useEffect, useRef, useState, useCallback } from "react";

const IsolatedTextareaImpl = React.forwardRef(function IsolatedTextarea(
  {
    value = "",
    onCommit,
    debounceMs = 300,
    className = "",
    style,
    ...rest
  },
  ref
) {
  const [local, setLocal] = useState(value ?? "");
  const debounceRef = useRef(null);
  const lastExternalRef = useRef(value ?? "");
  const localRef = useRef(local);
  localRef.current = local;

  // Se il parent aggiorna value dall'esterno (es. carica una nota
  // diversa o annulla), allineiamo il local state. Confrontiamo con
  // l'ultimo valore esterno noto per non sovrascrivere il typing
  // corrente dell'utente.
  useEffect(() => {
    const next = value ?? "";
    if (next !== lastExternalRef.current) {
      lastExternalRef.current = next;
      setLocal(next);
    }
  }, [value]);

  const commit = useCallback((v) => {
    lastExternalRef.current = v;
    if (typeof onCommit === "function") onCommit(v);
  }, [onCommit]);

  const scheduleCommit = useCallback((v) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      commit(v);
    }, Math.max(0, debounceMs));
  }, [commit, debounceMs]);

  const handleChange = useCallback((e) => {
    const v = e.target.value;
    setLocal(v);
    scheduleCommit(v);
  }, [scheduleCommit]);

  const handleBlur = useCallback(() => {
    // Blur → commit immediato, cancella eventuale timer in coda.
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (localRef.current !== lastExternalRef.current) {
      commit(localRef.current);
    }
  }, [commit]);

  useEffect(() => {
    // Cleanup: se il componente si smonta con un commit in coda,
    // lo eseguiamo subito così il parent non perde caratteri.
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        if (localRef.current !== lastExternalRef.current && typeof onCommit === "function") {
          onCommit(localRef.current);
        }
      }
    };
    // onCommit intenzionalmente stabile (parent responsabile della memoizzazione)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <textarea
      ref={ref}
      value={local}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      style={style}
      {...rest}
    />
  );
});

export const IsolatedTextarea = React.memo(IsolatedTextareaImpl);
export default IsolatedTextarea;
